// src/features/mocktests/components/SpeakingSection.js

import React, { useState, useRef } from "react";
import { useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  LinearProgress,
  Chip,
  Divider,
  Alert,
  Popover,
  CircularProgress,
  Badge,
  Fade,
  Tooltip,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LockIcon from "@mui/icons-material/Lock";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  ExpandLess,
  ExpandMore,
  CheckRounded as CheckRoundedIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";

import {
  useUploadAudioMutation,
  useCreateSpeakingAnswerMutation,
} from "../mockTestSlice";
import TextHighlighterWrapper from "./TextHighlighterWrapper";
import NotesModal from "./NotesModal";
import HtmlRenderer from "./HtmlRenderer";

/* ---------------------------------------------------------------
   Enhanced Gradient Countdown (Ring + Pie + Center Icon)
--------------------------------------------------------------- */
const EnhancedCountdown = ({
  size = 56,
  stroke = 5,
  progress = 0, // 0..100
  showPie = true, // show inside pie fill
  showRing = true, // show outer progress ring
  idSuffix = "speaking",
}) => {
  const p = Math.min(100, Math.max(0, progress));
  const finished = p >= 100;

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c;
  const offset = c * (1 - p / 100);

  // ------- PIE PATH (sector fill) -------
  const angle = (p / 100) * Math.PI * 2;
  const cx = size / 2;
  const cy = size / 2;
  const x = cx + r * Math.cos(-Math.PI / 2 + angle);
  const y = cy + r * Math.sin(-Math.PI / 2 + angle);
  const largeArc = angle > Math.PI ? 1 : 0;
  const piePath =
    p <= 0
      ? ""
      : `
    M ${cx} ${cy}
    L ${cx} ${cy - r}
    A ${r} ${r} 0 ${largeArc} 1 ${x} ${y}
    Z
  `;

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <linearGradient id={`grad-${idSuffix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#9D2235" />
            <stop offset="100%" stopColor="#5d1220" />
          </linearGradient>
          <radialGradient id={`soft-${idSuffix}`} cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="rgba(157,34,53,0.10)" />
            <stop offset="100%" stopColor="rgba(157,34,53,0.0)" />
          </radialGradient>
        </defs>

        {/* Soft background plate for a card-ish look */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#soft-${idSuffix})`} />

        {/* Track */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(157,34,53,0.18)"
            strokeWidth={stroke}
          />
        )}

        {/* Pie fill from center (sector) */}
        {showPie && p > 0 && (
          <path d={piePath} fill={`url(#grad-${idSuffix})`} />
        )}

        {/* Progress Ring on top */}
        {showRing && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#grad-${idSuffix})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${dash}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )}

        {/* Subtle glow when finished */}
        {finished && (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="#9D2235"
            strokeOpacity="0.25"
            strokeWidth={2}
          />
        )}
      </svg>

      {/* Center icon */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "transform 180ms ease",
          transform: finished ? "scale(1.05)" : "scale(1)",
        }}
      >
        {finished ? (
          <CheckRoundedIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        ) : (
          <AccessTimeIcon
            sx={{ fontSize: Math.round(size * 0.34), color: "#7a182a" }}
          />
        )}
      </Box>
    </Box>
  );
};

/* ---------------------------------------------------------------
   SpeakingSection Component
--------------------------------------------------------------- */

export default function SpeakingSection({
  speakingData,
  currentPart = 1,
  studentAttemptId,
  speakingTestId,
  setLocalSpeakingData,
  setCurrentPart,
  setCurrentOffset,
  navigate,
  speakingCardIds = [],
  currentCardIndex = 0,
  setCurrentCardIndex,
  allSpeakingAudios,
  setAllSpeakingAudios,
  isAiBased = false,
  updateTestAttempt = null,
  bundleId = null, // Bundle ID for browser back navigation
  speakingTestCompleted = false, // Current completion status
  isSpeakingSubmitted = false, // If true, disable recording controls - test already submitted
  onShowResultsPopup = null, // Handler to show results popup
  notes = [], // Notes from parent - persists across all parts
  onAddNote = null, // Handler from parent
  highlights = [], // Highlights from parent - persists across all parts
  onAddHighlight = null, // Handler from parent
  onRemoveHighlight = null, // Handler from parent
}) {
  // Notes modal state
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const { testId } = useParams();
  const navigationRef = useRef(navigate);

  // Track current question within a part
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Reset question index when moving to a new part
  useEffect(() => {
    setCurrentQuestionIndex(0);
  }, [currentCardIndex]);

  // Update navigation ref when navigate changes
  useEffect(() => {
    navigationRef.current = navigate;
  }, [navigate]);

  const location = useLocation();

  // Handle browser back button - navigate to main screen
  useEffect(() => {
    const handlePopState = (event) => {
      // Prevent default back navigation
      event.preventDefault();
      // Navigate to main mock test screen or dashboard
      if (testId) {
        // Detect test type from URL
        const path = location.pathname.toLowerCase();
        let backPath;
        if (bundleId) {
          backPath = `/app/bundles/${bundleId}`;
        } else if (path.includes("/mini-mock-test/")) {
          backPath = `/mini-mock-test/${testId}`;
        } else if (path.includes("/class-test/")) {
          backPath = `/class-test/${testId}`;
        } else {
          backPath = `/mock-test/${testId}`;
        }
        navigationRef.current(backPath);
      } else {
        navigationRef.current("/dashboard");
      }
    };

    // Add popstate event listener
    window.addEventListener("popstate", handlePopState);

    // Push current state to allow back button to trigger our handler
    window.history.pushState(null, "", window.location.pathname);

    // Cleanup
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [testId, bundleId]);

  // Timer states (must be inside function body)
  const [prepTimeLeft, setPrepTimeLeft] = useState(60); // Part 2: 1 minute prep, Part 1&3: 3 seconds countdown
  const [prepActive, setPrepActive] = useState(false); // Will be activated after audio finishes

  // New state to track audio playback and countdown
  const [isQuestionAudioPlaying, setIsQuestionAudioPlaying] = useState(false);
  const [countdown, setCountdown] = useState(3); // 3-second countdown for Parts 1 & 3
  const [showCountdown, setShowCountdown] = useState(false);

  // Recording timer states
  const [recordTimeLeft, setRecordTimeLeft] = useState(120); // Default 2 minutes for Parts 1&3, will be set to 2min for Part 2
  const [recordTimerActive, setRecordTimerActive] = useState(false);

  // Text-to-Speech states
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(true);
  const [speechPaused, setSpeechPaused] = useState(false);
  const [currentUtterance, setCurrentUtterance] = useState(null);
  const speechSynthRef = useRef(null);

  // Initialize speech synthesis
  useEffect(() => {
    if ("speechSynthesis" in window) {
      speechSynthRef.current = window.speechSynthesis;
      setSpeechSupported(true);
    } else {
      setSpeechSupported(false);
      console.warn("Text-to-speech not supported in this browser");
    }

    // Cleanup on unmount
    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  // Function to speak text
  const speakText = useCallback(
    (text) => {
      if (!speechSynthRef.current || !speechSupported) return;

      // Cancel any ongoing speech
      speechSynthRef.current.cancel();

      // Remove HTML tags and clean the text
      const cleanText = text
        .replace(/<[^>]*>/g, "") // Remove HTML tags
        .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
        .replace(/&amp;/g, "&") // Replace HTML entities
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();

      if (!cleanText) return;

      const utterance = new SpeechSynthesisUtterance(cleanText);

      // Configure voice settings for IELTS-like quality
      utterance.lang = "en-GB"; // British English for IELTS
      utterance.rate = 0.9; // Slightly slower for clarity
      utterance.pitch = 1.0; // Normal pitch
      utterance.volume = 1.0; // Full volume

      // Try to use a British English voice if available
      const voices = speechSynthRef.current.getVoices();
      const britishVoice = voices.find(
        (voice) => voice.lang === "en-GB" || voice.lang.startsWith("en-GB"),
      );
      if (britishVoice) {
        utterance.voice = britishVoice;
      }

      utterance.onstart = () => {
        setIsSpeaking(true);
        setSpeechPaused(false);
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setSpeechPaused(false);
        setCurrentUtterance(null);
      };

      utterance.onerror = (event) => {
        console.error("Speech synthesis error:", event);
        setIsSpeaking(false);
        setSpeechPaused(false);
        setCurrentUtterance(null);
      };

      // Store the current utterance for pause/resume
      setCurrentUtterance(utterance);
      speechSynthRef.current.speak(utterance);
    },
    [speechSupported],
  );

  // Function to stop speaking
  const stopSpeaking = () => {
    if (speechSynthRef.current) {
      speechSynthRef.current.cancel();
      setIsSpeaking(false);
      setSpeechPaused(false);
      setCurrentUtterance(null);
    }
  };

  // Function to pause speaking
  const pauseSpeaking = () => {
    if (speechSynthRef.current && isSpeaking) {
      speechSynthRef.current.pause();
      setSpeechPaused(true);
    }
  };

  // Function to resume speaking
  const resumeSpeaking = () => {
    if (speechSynthRef.current && speechPaused) {
      speechSynthRef.current.resume();
      setSpeechPaused(false);
    }
  };

  // Stop speech when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      stopSpeaking();
    };
  }, []);

  // --- UI and Progress ---
  const speakingTest = speakingData?.speakingTest;
  const speakingParts = speakingTest?.speakingParts || [];

  // Get current speaking part based on currentCardIndex
  const currentSpeakingPart = speakingParts[currentCardIndex] || {};
  const currentPartQuestions = currentSpeakingPart?.questions || [];

  // Get the current question to display
  const currentQuestion = currentPartQuestions[currentQuestionIndex];

  // Compact view flag for short-height screens (avoid vertical scrolling)
  const [isShortScreen, setIsShortScreen] = useState(false);

  // Timer progress calculation for enhanced countdown
  const getSpeakingTimerProgress = () => {
    if (showCountdown) {
      // During 3-second countdown: show progress
      return ((3 - countdown) / 3) * 100;
    } else if (prepActive) {
      // During preparation time: show progress of prep timer (Part 2)
      const totalPrepTime = currentSpeakingPart.partNumber === 2 ? 60 : 3;
      return ((totalPrepTime - prepTimeLeft) / totalPrepTime) * 100;
    } else {
      // During recording time: show progress of record timer
      const totalRecordTime = 120; // 2 minutes for all parts
      return ((totalRecordTime - recordTimeLeft) / totalRecordTime) * 100;
    }
  };

  const speakingTimerProgress = getSpeakingTimerProgress();

  // Format timer display for header
  const formatSpeakingTimerDisplay = () => {
    if (showCountdown) {
      return `00:0${countdown}`;
    }
    const timeLeft = prepActive ? prepTimeLeft : recordTimeLeft;
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0",
    )}`;
  };

  // 3-second countdown timer for Parts 1 & 3
  useEffect(() => {
    if (!showCountdown) return;
    if (countdown <= 0) {
      setShowCountdown(false);
      // Auto-start recording after countdown (only if not already submitted)
      if (!isSpeakingSubmitted) {
        handleStart();
      }
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [showCountdown, countdown, isSpeakingSubmitted]);

  // Preparation timer effect (Part 2 only)
  useEffect(() => {
    if (!prepActive) return;
    if (prepTimeLeft <= 0) {
      setPrepActive(false);
      // Auto-start recording after prep time (only if not already submitted)
      if (!isSpeakingSubmitted) {
        handleStart();
      }
      return;
    }
    const timer = setInterval(() => {
      setPrepTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [prepActive, prepTimeLeft, isSpeakingSubmitted]);

  // Recording / Upload state
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Audio playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  // Recording timer effect (must be after isRecording is defined)
  useEffect(() => {
    // Only run timer if actively recording and not paused
    if (!recordTimerActive || !isRecording || isPaused) return;
    if (recordTimeLeft <= 0) {
      setRecordTimerActive(false);
      setRecordTimeLeft(120); // Reset to 2 minutes
      if (isRecording) {
        handleStop();
      }
      return;
    }
    const timer = setInterval(() => {
      setRecordTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [recordTimerActive, recordTimeLeft, isRecording, isPaused]);

  // Detect short-height screens and enable compact mode
  useEffect(() => {
    const checkHeight = () => {
      try {
        setIsShortScreen(window?.innerHeight < 720);
      } catch (err) {
        // ignore in non-browser environments
      }
    };

    checkHeight();
    window.addEventListener("resize", checkHeight);
    return () => window.removeEventListener("resize", checkHeight);
  }, []);

  // Audio upload + speaking submission
  const [audioMeta, setAudioMeta] = useState(null);
  const [uploadAudio, { isLoading: isUploading }] = useUploadAudioMutation();
  const [createSpeakingAnswer, { isLoading: isSubmitting }] =
    useCreateSpeakingAnswerMutation();

  // This ref holds ALL audios for submit

  // UI feedback
  const [successOpen, setSuccessOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // New popup states for API response
  const [submissionPopupOpen, setSubmissionPopupOpen] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState(null);

  // Highlighting and notes functionality now handled by TextHighlighterWrapper

  // Helpers
  function resetCurrentPartState() {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setIsRecording(false);
    setIsPaused(false);
    setAudioMeta(null);
    setUploadProgress(0);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }

  // Audio playback controls
  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  // Recording handlers
  const handleStart = () => {
    // Don't start recording if speaking is already submitted
    if (isSpeakingSubmitted) {
      console.log("Speaking already submitted, recording disabled");
      return;
    }

    if (mediaRecorderRef.current?.state === "paused") {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      setRecordTimerActive(true);
      return;
    }
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        const recorder = new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;
        chunksRef.current = [];
        recorder.ondataavailable = (e) => chunksRef.current.push(e.data);
        recorder.onstop = () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          setRecordedBlob(blob);
          setRecordedUrl(URL.createObjectURL(blob));
        };
        recorder.start();
        setIsRecording(true);
        setRecordTimerActive(true);
        setRecordTimeLeft(120); // Set to 2 minutes for all parts
      })
      .catch(() => {
        const errorResponse = {
          status: "error",
          success: false,
          message: "Error accessing microphone. Please check your permissions.",
          errors: ["Microphone access denied"],
        };
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);
      });
  };

  const handlePause = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      setRecordTimerActive(false);
    }
  };

  const handleStop = () => {
    if (mediaRecorderRef.current?.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  };
  const handleDeleteRecording = () => {
    setRecordedBlob(null);
    setRecordedUrl(null);
    setAudioMeta(null);
    setUploadProgress(0);
    setRecordTimeLeft(120); // Reset timer to 2:00
    setRecordTimerActive(false); // Stop timer
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  // AUDIO UPLOAD FUNCTION
  const handleUploadAudio = async () => {
    try {
      setUploadProgress(0);
      let formData = new FormData();
      if (recordedBlob) {
        formData.append("audio", recordedBlob, `recorded-${Date.now()}.webm`);
      } else {
        // Create error response for missing audio
        const errorResponse = {
          status: 400,
          data: {
            success: 0,
            message: "Please record audio first.",
            error: "No audio file provided",
            data: {},
          },
        };
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);
        return null;
      }
      // Upload to server
      const res = await uploadAudio(formData).unwrap();
      setUploadProgress(100);

      // Flatten audio object for uniformity
      let flatAudioObj =
        res?.uploadToBucket?.[0] || res?.data?.uploadToBucket?.[0] || res;

      setAudioMeta(flatAudioObj);
      // Audio upload success - no popup needed here as this is intermediate step
      return flatAudioObj;
    } catch (err) {
      // Create error response for upload failure
      const errorResponse = {
        status: err.status || 500,
        data: {
          success: 0,
          message: "Audio upload failed. Please try again.",
          error: err.message || "Upload error occurred",
          data: err.data || {},
        },
      };
      setSubmissionResponse(errorResponse);
      setSubmissionPopupOpen(true);
      return null;
    }
  };

  // SUBMIT/NEXT FUNCTION - handles both question and part navigation
  const handleNextOrSubmit = async () => {
    // Get current part questions
    const currentSpeakingPart = speakingParts[currentCardIndex] || {};
    const currentPartQuestions = currentSpeakingPart?.questions || [];
    const currentQuestionData = currentPartQuestions[currentQuestionIndex];

    // Check if there are more questions in current part
    const isLastQuestionInPart =
      currentQuestionIndex >= currentPartQuestions.length - 1;

    // Upload audio for current question
    let audioRes = audioMeta;
    if (!audioRes) {
      audioRes = await handleUploadAudio();
      if (!audioRes) return;
    }

    // Always flatten
    let flatAudioObj = audioRes?.uploadToBucket?.[0] || audioRes;

    // Add speaking metadata to the audio object
    const audioWithMetadata = {
      ...flatAudioObj,
      speakingPartId: currentSpeakingPart._id || "",
      questionNumber: currentQuestionData?.questionNumber || "",
      questionId: currentQuestionData?._id || "",
    };

    // Update the parent's audio array for every question
    setAllSpeakingAudios((prev) => [...prev, audioWithMetadata]);

    resetCurrentPartState();

    // If not the last question in the part, just move to next question
    if (!isLastQuestionInPart) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setAudioMeta(null);
      return;
    }

    // If last question of part but NOT the last card, move to next part
    if (currentCardIndex < speakingCardIds.length - 1) {
      setCurrentCardIndex(currentCardIndex + 1);
      setCurrentPart && setCurrentPart((prev) => prev + 1);
      setCurrentOffset && setCurrentOffset((prev) => prev + 1);
      setCurrentQuestionIndex(0); // Reset to first question of next part
      setAudioMeta(null);
    } else if (currentCardIndex === speakingCardIds.length - 1) {
      // Last question of last part - submit all audios
      try {
        // submit ALL audios accumulated in parent (already includes metadata from all questions)
        const submitPayload = {
          studentAttemptId,
          speakingTestId,
          isAiBased: true,
          speakingAudios: [...allSpeakingAudios, audioWithMetadata], // include last one with metadata
        };
        const response = await createSpeakingAnswer(submitPayload).unwrap();

        // Check if the response contains an error message (even with status 200)
        if (
          response?.data?.message &&
          response.data.message.includes("already completed")
        ) {
          console.log(
            "Speaking test already completed, showing popup and setting completion flag",
          );
          // Show popup with the error message instead of success
          setSubmissionResponse(response);
          setSubmissionPopupOpen(true);
          // Set completion flag to prevent further API calls

          return; // Exit early, don't proceed with success flow
        }

        console.log(
          "Speaking submission successful, calling onShowResultsPopup",
        );
        
        // Call parent's handler to show TestResultsPopup instead of submission popup
        if (onShowResultsPopup) {
          onShowResultsPopup();
        } else {
          // Fallback: show submission popup if onShowResultsPopup is not provided
          setSubmissionResponse(response);
          setSubmissionPopupOpen(true);
        }

        // Update test attempt completion status for simulation mode
        if (updateTestAttempt && studentAttemptId) {
          try {
            console.log(
              "Updating test attempt completion for simulation mode:",
              studentAttemptId,
            );
            await updateTestAttempt({
              studentAttemptId,
              isCompleted: true,
            });
            console.log("Test attempt updated successfully");
          } catch (error) {
            console.error("Error updating test attempt:", error);
            // Don't fail the submission if this update fails
          }
        }

        // Reset states but don't change currentCardIndex to avoid triggering useEffect
        setAllSpeakingAudios([]); // reset after submit
        resetCurrentPartState();
        setAudioMeta(null);
        // Don't reset these to avoid triggering useEffect in SpeakingPage
        // setCurrentPart && setCurrentPart(1);
        // setCurrentOffset && setCurrentOffset(0);
        // setCurrentCardIndex && setCurrentCardIndex(0);
      } catch (err) {
        console.error("Speaking submission error:", err);

        // Create an error response object to show in popup
        const errorResponse = {
          status: err.status || 500,
          data: {
            success: 0,
            message:
              err.data?.message ||
              "Failed to submit speaking answer. Please try again.",
            error: err.message || "Network or server error occurred",
            data: err.data || {},
          },
        };

        // Show error in popup instead of snackbar
        setSubmissionResponse(errorResponse);
        setSubmissionPopupOpen(true);
      }
    }
  };

  // Handle closing the submission popup and navigating
  const handleCloseSubmissionPopup = () => {
    setSubmissionPopupOpen(false);
    setSubmissionResponse(null);
    setTimeout(() => {
      // Navigate back to the appropriate test page based on available testId
      const redirectTestId = testId;
      if (redirectTestId) {
        // Detect test type from URL
        const path = location.pathname.toLowerCase();
        let backPath;
        if (bundleId) {
          backPath = `/app/bundles/${bundleId}`;
        } else if (path.includes("/mini-mock-test/")) {
          backPath = `/mini-mock-test/${redirectTestId}`;
        } else if (path.includes("/class-test/")) {
          backPath = `/class-test/${redirectTestId}`;
        } else {
          backPath = `/mock-test/${redirectTestId}`;
        }
        navigate(backPath);
      } else {
        navigate("/dashboard");
      }
    }, 500);
  };

  // Auto-play question when it changes and handle different part timings
  useEffect(() => {
    if (currentQuestion?.questions) {
      // Reset all states when question changes
      setShowCountdown(false);
      setPrepActive(false);
      setRecordTimerActive(false);
      setIsRecording(false);
      setIsQuestionAudioPlaying(true);

      // Clear any existing recordings when moving to new question
      setRecordedBlob(null);
      setRecordedUrl(null);
      setAudioMeta(null);

      // Set appropriate timer values based on part number
      const partNumber = currentSpeakingPart.partNumber;
      if (partNumber === 2) {
        // Part 2: 1 minute preparation, 2 minutes recording
        setPrepTimeLeft(60);
        setRecordTimeLeft(120);
      } else {
        // Parts 1 & 3: 3-second countdown, 2 minutes recording
        setCountdown(3);
        setPrepTimeLeft(3);
        setRecordTimeLeft(120);
      }

      // Stop any existing speech
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }

      // Start audio playback after a short delay to ensure UI is ready
      const audioTimeout = setTimeout(() => {
        // Play the question audio with enhanced settings
        const cleanText = currentQuestion.questions
          .replace(/<[^>]*>/g, "") // Remove HTML tags
          .replace(/&nbsp;/g, " ") // Replace &nbsp; with space
          .replace(/&amp;/g, "&") // Replace HTML entities
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .trim();

        if (cleanText && speechSynthRef.current) {
          const utterance = new SpeechSynthesisUtterance(cleanText);
          utterance.lang = "en-GB"; // British English for IELTS
          utterance.rate = 0.9; // Slightly slower for clarity
          utterance.pitch = 1.0;
          utterance.volume = 1.0;

          // Try to use a British English voice if available
          const voices = speechSynthRef.current.getVoices();
          const britishVoice = voices.find(
            (voice) => voice.lang === "en-GB" || voice.lang.startsWith("en-GB"),
          );
          if (britishVoice) {
            utterance.voice = britishVoice;
          }

          utterance.onstart = () => {
            setIsSpeaking(true);
            setIsQuestionAudioPlaying(true);
          };

          utterance.onend = () => {
            setIsSpeaking(false);
            setIsQuestionAudioPlaying(false);

            // After audio finishes, start appropriate timer based on part
            const partNumber = currentSpeakingPart.partNumber;
            if (partNumber === 2) {
              // Part 2: Start 1-minute preparation
              setPrepActive(true);
            } else {
              // Parts 1 & 3: Start 3-second countdown
              setShowCountdown(true);
              setCountdown(3);
            }
          };

          utterance.onerror = () => {
            setIsSpeaking(false);
            setIsQuestionAudioPlaying(false);
            // On error, still proceed with timer
            const partNumber = currentSpeakingPart.partNumber;
            if (partNumber === 2) {
              setPrepActive(true);
            } else {
              setShowCountdown(true);
              setCountdown(3);
            }
          };

          speechSynthRef.current.speak(utterance);
        }
      }, 1000); // 1 second delay to allow UI to settle

      return () => clearTimeout(audioTimeout);
    }
  }, [
    currentQuestionIndex,
    currentCardIndex,
    currentQuestion?.questions,
    currentSpeakingPart.partNumber,
  ]);

  const totalParts = speakingCardIds.length || 4;
  const isLastCard = currentCardIndex === totalParts - 1;
  const isLastQuestionInPart =
    currentQuestionIndex >= currentPartQuestions.length - 1;

  // Get part title based on part number
  const getPartTitle = (partNumber) => {
    switch (partNumber) {
      case 1:
        return "Introduction and Interview";
      case 2:
        return "Long Turn";
      case 3:
        return "Discussion";
      default:
        return `Part ${partNumber}`;
    }
  };

  // If test is completed and no speaking data, show a minimal interface
  if (speakingTestCompleted && !speakingData) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5" sx={{ color: "#9D2235", mb: 2 }}>
          Speaking Test Completed
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Your speaking responses have been submitted.
        </Typography>

        {/* Show the popup dialogs even with minimal interface */}
        {/* Submission Response Popup */}
        <Dialog
          open={submissionPopupOpen}
          onClose={handleCloseSubmissionPopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              bgcolor: submissionResponse?.data?.message?.includes(
                "already completed",
              )
                ? "#f44336"
                : "#9D2235",
              color: "white",
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight="bold">
                {submissionResponse?.data?.message?.includes(
                  "already completed",
                )
                  ? "Submission Notice"
                  : "Submission Successful!"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {submissionResponse?.data?.message?.includes(
              "already completed",
            ) ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  ⚠️ Test Already Completed
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {submissionResponse.data.message}
                </Typography>
              </Alert>
            ) : (
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="body1" fontWeight="bold">
                  🎉 All speaking parts have been recorded and submitted
                  successfully!
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Your audio responses are now being processed for evaluation.
                </Typography>
              </Alert>
            )}

            {submissionResponse && (
              <Box>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ color: "#9D2235", fontWeight: "bold" }}
                >
                  📋 Speaking Answer API Response:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}
                >
                  Response from:{" "}
                  <code>/api/v1/en/student/speaking-answer/create</code>
                </Typography>
                <Box
                  sx={{
                    bgcolor: "#f8f9fa",
                    p: 2,
                    borderRadius: 2,
                    maxHeight: "300px",
                    overflow: "auto",
                    border: "2px solid #e9ecef",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: '"Courier New", Consolas, monospace',
                      fontSize: "0.85rem",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      color: "#2d3748",
                    }}
                  >
                    {JSON.stringify(submissionResponse, null, 2)}
                  </pre>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 1,
                    color: "text.secondary",
                    fontStyle: "italic",
                  }}
                >
                  💡 This shows the server response from the speaking answer
                  submission API.
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: "center" }}>
            <Button
              onClick={handleCloseSubmissionPopup}
              variant="contained"
              size="large"
              startIcon={<CheckCircleIcon />}
              sx={{
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                px: 4,
                py: 1.5,
                fontSize: "1rem",
                fontWeight: "bold",
              }}
            >
              🏠 Return to Test Menu
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <>
      {/* ===== Header ===== */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          bgcolor: "#F7F7F7",
          px: { xs: 0.5, sm: 0.5, md: 0.5, lg: 0.5 },
          py: { xs: 0.05, sm: 0.1 },
          position: "sticky",
          top: 0,
          zIndex: 1000,
          transition: "all 0.3s ease-in-out",
        }}
      >
        <Card
          elevation={1}
          sx={{
            width: "100%",
            mb: { xs: 0.5, sm: 1 },
            p: { xs: 0.5, sm: 0.8, md: 1, lg: 1.2 },
            bgcolor: "#fff",
            borderRadius: { xs: 1, sm: 1.5, md: 2 },
            boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              alignItems: "center",
              gridTemplateAreas: {
                xs: `"left center right"`,
                sm: `"left center right"`,
                md: `"left center right"`,
                lg: `"left center right"`,
              },
              gridTemplateColumns: {
                xs: "1fr 2fr 1fr",
                sm: "1fr 2fr 1fr",
                md: "1fr 2fr 1fr",
                lg: "1fr 2fr 1fr",
              },
              gridTemplateRows: "auto",
              columnGap: { xs: 0.5, sm: 0.8, md: 1.2, lg: 1.5 },
              rowGap: 0,
              minWidth: 0,
              minHeight: { xs: 35, sm: 40, md: 45 },
            }}
          >
            {/* LEFT - Speaking Info */}
            <Box
              sx={{
                gridArea: "left",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                width: "100%",
                gap: { xs: 0.2, sm: 0.5, md: 1 },
                py: { xs: 0.1, sm: 0.2 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  minWidth: 0,
                  flexShrink: 1,
                  width: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#9D2235",
                    fontSize: { xs: "0.7rem", sm: "0.85rem", md: "0.95rem" },
                    lineHeight: 1.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  Speaking Module
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 700,
                    color: "#7a7a7a",
                    fontSize: { xs: "0.6rem", sm: "0.75rem", md: "0.8rem" },
                    lineHeight: 1.1,
                    letterSpacing: 0.1,
                    whiteSpace: "nowrap",
                  }}
                >
                  <b>Part {currentCardIndex + 1}</b>
                </Typography>
              </Box>
            </Box>

            {/* CENTER - Speaking Navigation Chips */}
            <Box
              sx={{
                gridArea: "center",
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                gap: { xs: 0.2, sm: 0.8, md: 1.2, lg: 1.5 },
                minWidth: 0,
                justifyContent: "center",
                py: { xs: 0.1, sm: 0.3, md: 0.4 },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  px: 0,
                  py: 0,
                  minWidth: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    display: "inline-flex",
                    gap: { xs: 0.2, sm: 0.3, md: 0.4, lg: 0.5 },
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {speakingCardIds.map((cardId, idx) => (
                    <Box
                      key={idx}
                      onClick={() => setCurrentCardIndex(idx)}
                      sx={{
                        width: {
                          xs: isShortScreen ? 14 : 18,
                          sm: 20,
                          md: 24,
                          lg: 28,
                        },
                        height: {
                          xs: isShortScreen ? 14 : 18,
                          sm: 20,
                          md: 24,
                          lg: 28,
                        },
                        borderRadius: "50%",
                        border: "1.5px solid #9D2235",
                        bgcolor:
                          currentCardIndex === idx ? "#9D2235" : "transparent",
                        color: currentCardIndex === idx ? "#fff" : "#9D2235",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: {
                          xs: isShortScreen ? "0.5rem" : "0.6rem",
                          sm: "0.65rem",
                          md: "0.75rem",
                          lg: "0.85rem",
                        },
                        cursor: "pointer",
                        transition: "background 0.15s",
                        "&:hover": {
                          bgcolor:
                            currentCardIndex === idx
                              ? "#7a182a"
                              : "rgba(157,34,53,0.08)",
                        },
                        "&:active": {},
                        flex: "0 0 auto",
                      }}
                    >
                      {idx + 1}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* RIGHT - Notes Only */}
            <Box
              sx={{
                gridArea: "right",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.3, sm: 0.6, md: 0.8 },
              }}
            >
              {/* Notes Icon */}
              <Badge
                badgeContent={notes?.length || 0}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: "#fff",
                    color: "#9D2235",
                    fontWeight: "bold",
                    fontSize: { xs: "0.35rem", sm: "0.45rem", md: "0.5rem" },
                    minWidth: { xs: 6, sm: 10, md: 12 },
                    height: { xs: 6, sm: 10, md: 12 },
                    boxShadow: "0 2px 4px rgba(157,34,53,0.2)",
                  },
                }}
              >
                <IconButton
                  onClick={() => setNotesModalOpen(true)}
                  sx={{
                    background:
                      "linear-gradient(135deg, #9D2235 0%, #7a182a 100%)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow:
                      "0 4px 12px rgba(157,34,53,0.25), 0 2px 4px rgba(157,34,53,0.15)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #7a182a 0%, #5d1220 100%)",
                      transform: "translateY(-1px)",
                      boxShadow:
                        "0 6px 16px rgba(157,34,53,0.3), 0 4px 8px rgba(157,34,53,0.2)",
                    },
                    width: { xs: 18, sm: 28, md: 32 },
                    height: { xs: 18, sm: 28, md: 32 },
                  }}
                >
                  <StickyNote2Icon
                    sx={{
                      fontSize: { xs: "0.6rem", sm: "0.9rem", md: "1rem" },
                      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.3))",
                    }}
                  />
                </IconButton>
              </Badge>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Main Speaking Content */}
      <Box
        className="w-full px-4 py-8"
        sx={{
          pt: { xs: "16px", sm: "24px", md: "32px" },
          px: { xs: 1, sm: 2, md: 4 },
          py: { xs: isShortScreen ? 1 : 2, sm: 4, md: 8 },
          transition: "padding-top 0.3s ease-in-out",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: {
            xs: isShortScreen ? "40vh" : "calc(100vh - 120px)",
            sm: isShortScreen ? "50vh" : "calc(100vh - 140px)",
            md: isShortScreen ? "60vh" : "70vh",
          },
        }}
      >
        {/* Speaking Question Card - IELTS Style Interface */}
        {currentQuestion ? (
          <Card
            sx={{
              maxWidth: { xs: "100%", sm: 600, md: 700 },
              width: "100%",
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              border: "3px dashed #9D2235",
              position: "relative",
              overflow: "visible",
              mx: { xs: 0, sm: "auto" },
            }}
          >
            {/* Corner decorations */}
            {[
              { top: -8, left: -8 },
              { top: -8, right: -8 },
              { bottom: -8, left: -8 },
              { bottom: -8, right: -8 },
            ].map((position, index) => (
              <Box
                key={index}
                sx={{
                  position: "absolute",
                  ...position,
                  width: { xs: 12, sm: 16 },
                  height: { xs: 12, sm: 16 },
                  backgroundColor: "#9D2235",
                  transform: "rotate(45deg)",
                }}
              />
            ))}

            <CardContent
              sx={{
                p: {
                  xs: isShortScreen ? 1.5 : 2,
                  sm: isShortScreen ? 2 : 3,
                  md: isShortScreen ? 3 : 5,
                },
              }}
            >
              <TextHighlighterWrapper
                onAddNote={onAddNote}
                excludeHeaders={false}
                highlights={highlights}
                onAddHighlight={onAddHighlight}
                onRemoveHighlight={onRemoveHighlight}
                partKey={`speaking-part-${currentCardIndex}-${currentQuestionIndex}`}
              >
                {/* Header */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: { xs: isShortScreen ? 1 : 2, sm: 3, md: 4 },
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      color: "#9D2235",
                      fontWeight: "bold",
                      borderBottom: "2px solid #9D2235",
                      pb: 1,
                      mb: { xs: isShortScreen ? 0.5 : 1, sm: 2 },
                      display: "inline-block",
                      minWidth: {
                        xs: isShortScreen ? "160px" : "200px",
                        sm: "220px",
                        md: "300px",
                      },
                      fontSize: {
                        xs: isShortScreen ? "0.95rem" : "1.2rem",
                        sm: "1.3rem",
                        md: "1.6rem",
                      },
                    }}
                  >
                    {getPartTitle(currentSpeakingPart.partNumber)}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#9D2235",
                      fontWeight: 600,
                      mb: { xs: isShortScreen ? 1 : 1.5, sm: 2, md: 3 },
                      fontSize: {
                        xs: isShortScreen ? "0.85rem" : "1rem",
                        sm: "1.05rem",
                        md: "1.25rem",
                      },
                    }}
                  >
                    Question {currentQuestionIndex + 1} of Part {currentSpeakingPart.partNumber}
                  </Typography>

                  {/* Question with speaker button */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: 2,
                      mb: { xs: isShortScreen ? 1.5 : 2, sm: 3, md: 4 },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        color: "#333",
                        fontWeight: 500,
                        fontSize: {
                          xs: isShortScreen ? "0.95rem" : "1.25rem",
                          sm: "1.4rem",
                          md: "2rem",
                        },
                        lineHeight: { xs: 1.2, sm: 1.3, md: 1.4 },
                        flex: 1,
                        textAlign: "left",
                        userSelect: "text",
                        cursor: "text",
                      }}
                    >
                      <HtmlRenderer
                        content={currentQuestion.questions}
                        component="span"
                        sx={{
                          color: "inherit",
                          userSelect: "text",
                          cursor: "text",
                        }}
                      />
                    </Typography>
                  </Box>
                </Box>

                {/* Question Heading (if exists and not EXAMPLE) */}
                {currentQuestion.questionHeading && currentQuestion.questionHeading !== "EXAMPLE" && (
                  <Box
                    sx={{
                      mb: { xs: isShortScreen ? 0.75 : 1.5, sm: 2, md: 3 },
                      textAlign: "left",
                      userSelect: "text",
                      cursor: "text",
                    }}
                  >
                    <HtmlRenderer
                      content={currentQuestion.questionHeading}
                      component="div"
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        color: "#666",
                        whiteSpace: "pre-line",
                        lineHeight: 1.5,
                        userSelect: "text",
                        cursor: "text",
                      }}
                    />
                  </Box>
                )}

                {/* Audio Playing, Countdown, or Preparation Phase */}
                {(isQuestionAudioPlaying || showCountdown || prepActive) && (
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        width: { xs: 80, sm: 100, md: 120 },
                        height: { xs: 80, sm: 100, md: 120 },
                        borderRadius: "50%",
                        backgroundColor: isQuestionAudioPlaying
                          ? "#e3f2fd"
                          : showCountdown
                            ? "#fff3e0"
                            : "#f8f8f8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: {
                          xs: "0 auto 1.5rem auto",
                          sm: "0 auto 2rem auto",
                          md: "0 auto 3rem auto",
                        },
                        border: `3px solid ${isQuestionAudioPlaying ? "#9D2235" : showCountdown ? "#ff9800" : "#e0e0e0"}`,
                        boxShadow:
                          isQuestionAudioPlaying || showCountdown
                            ? "0 4px 16px rgba(0,0,0,0.2)"
                            : "0 4px 16px rgba(0,0,0,0.1)",
                        animation: isQuestionAudioPlaying
                          ? "pulse 2s ease-in-out infinite"
                          : "none",
                      }}
                    >
                      {isQuestionAudioPlaying ? (
                        <VolumeUpIcon
                          sx={{
                            fontSize: { xs: 32, sm: 40, md: 48 },
                            color: "#9D2235",
                          }}
                        />
                      ) : showCountdown ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <VolumeUpIcon
                            sx={{
                              color: "#9D2235",
                              fontSize: { xs: 18, sm: 20, md: 22 },
                            }}
                          />
                          <Typography
                            variant="h3"
                            sx={{
                              color: "#ff9800",
                              fontWeight: "bold",
                              fontSize: { xs: 24, sm: 32, md: 40 },
                            }}
                          >
                            {countdown}
                          </Typography>
                        </Box>
                      ) : (
                        <MicIcon
                          sx={{
                            fontSize: { xs: 32, sm: 40, md: 48 },
                            color: "#bbb",
                          }}
                        />
                      )}
                    </Box>

                    {isQuestionAudioPlaying && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <VolumeUpIcon
                          sx={{
                            color: "#9D2235",
                            fontSize: { xs: 18, sm: 20, md: 22 },
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#9D2235",
                            fontWeight: "bold",
                            fontSize: {
                              xs: "1rem",
                              sm: "1.1rem",
                              md: "1.25rem",
                            },
                          }}
                        >
                          Playing Question Audio...
                        </Typography>
                      </Box>
                    )}

                    {showCountdown && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <AccessTimeIcon
                          sx={{
                            color: "#ff9800",
                            fontSize: { xs: 18, sm: 20, md: 22 },
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#ff9800",
                            fontWeight: "bold",
                            fontSize: {
                              xs: "1rem",
                              sm: "1.1rem",
                              md: "1.25rem",
                            },
                          }}
                        >
                          Get Ready to Speak...
                        </Typography>
                      </Box>
                    )}

                    {prepActive && (
                      <>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={() => {
                            setPrepActive(false);
                            handleStart();
                          }}
                          sx={{
                            backgroundColor: "#9D2235",
                            color: "white",
                            fontSize: {
                              xs: "1rem",
                              sm: "1.1rem",
                              md: "1.2rem",
                            },
                            fontWeight: "bold",
                            py: { xs: 1.5, sm: 2, md: 2.5 },
                            px: { xs: 4, sm: 6, md: 8 },
                            borderRadius: 30,
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "#7a182a",
                            },
                            boxShadow: "0 6px 20px rgba(157, 34, 53, 0.4)",
                            transition: "all 0.3s ease",
                            mb: { xs: isShortScreen ? 0.5 : 1, sm: 1.5, md: 2 },
                          }}
                        >
                          <AccessTimeIcon
                            sx={{
                              color: "#fff",
                              mr: 1,
                              fontSize: { xs: 18, sm: 20, md: 22 },
                            }}
                          />
                          Preparation Time {formatSpeakingTimerDisplay()}
                        </Button>

                        {/* Skip Preparation button for Part 2 */}
                        <Box sx={{ mt: { xs: 1, sm: 1.5, md: 2 } }}>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setPrepActive(false);
                              handleStart();
                            }}
                            sx={{
                              borderColor: "#9D2235",
                              color: "#9D2235",
                              fontWeight: 600,
                              fontSize: {
                                xs: "0.8rem",
                                sm: "0.9rem",
                                md: "1rem",
                              },
                              py: { xs: 0.5, sm: 0.75, md: 1 },
                              px: { xs: 2, sm: 3, md: 4 },
                              "&:hover": {
                                borderColor: "#7a182a",
                                bgcolor: "rgba(157, 34, 53, 0.05)",
                              },
                            }}
                          >
                            Skip Preparation
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                )}

                {/* Recording Phase */}
                {!prepActive && !showCountdown && !isQuestionAudioPlaying && (
                  <Box sx={{ textAlign: "center" }}>
                    {/* Recording indicator */}
                    {isRecording && (
                      <Box
                        sx={{
                          mb: { xs: isShortScreen ? 0.5 : 1, sm: 1.5, md: 2 },
                        }}
                      >
                        <Chip
                          label={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <FiberManualRecordIcon
                                sx={{
                                  color: "#fff",
                                  fontSize: { xs: 10, sm: 12, md: 14 },
                                  animation: "pulse 1s infinite",
                                }}
                              />
                              Recording {isPaused ? "(Paused)" : ""}
                            </Box>
                          }
                          sx={{
                            backgroundColor: "#9D2235",
                            color: "white",
                            fontSize: {
                              xs: isShortScreen ? "0.62rem" : "0.75rem",
                              sm: "0.85rem",
                              md: "0.9rem",
                            },
                            fontWeight: "bold",
                            py: 0.5,
                            px: 1,
                          }}
                        />
                      </Box>
                    )}

                    {/* Waveform visualization placeholder */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: { xs: 50, sm: 65, md: 80 },
                        mb: { xs: isShortScreen ? 0.8 : 1.5, sm: 2, md: 3 },
                        backgroundColor: "#fafafa",
                        borderRadius: 3,
                        border: "2px solid #f0f0f0",
                        p: 2,
                      }}
                    >
                      {/* Generate animated waveform bars */}
                      {Array.from(
                        { length: isShortScreen ? 28 : 60 },
                        (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              width: isShortScreen ? "1.5px" : "2px",
                              height: `${
                                isRecording
                                  ? Math.random() * (isShortScreen ? 18 : 30) +
                                    (isShortScreen ? 3 : 6)
                                  : Math.random() * (isShortScreen ? 8 : 15) +
                                    (isShortScreen ? 1 : 3)
                              }px`,
                              backgroundColor: "#9D2235",
                              margin: "0 0.5px",
                              borderRadius: "1px",
                              opacity: isRecording
                                ? 0.6 + Math.random() * 0.4
                                : 0.3,
                              transition: "height 0.1s ease-in-out",
                            }}
                          />
                        ),
                      )}
                    </Box>

                    {/* Timer */}
                    <Typography
                      variant="h3"
                      sx={{
                        color: "#333",
                        fontWeight: "bold",
                        fontFamily: "monospace",
                        mb: { xs: 1.5, sm: 2, md: 3 },
                        fontSize: {
                          xs: isShortScreen ? "0.95rem" : "1.5rem",
                          sm: "1.6rem",
                          md: "2.5rem",
                        },
                      }}
                    >
                      {formatSpeakingTimerDisplay()}
                    </Typography>

                    {/* Controls */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: { xs: 1, sm: 1.5, md: 2 },
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Show disabled message when speaking is already submitted */}
                      {isSpeakingSubmitted ? (
                        <Alert
                          severity="info"
                          sx={{
                            borderRadius: 2,
                            py: 1,
                            px: 2,
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            Speaking test has already been submitted. You cannot
                            record again.
                          </Typography>
                        </Alert>
                      ) : (
                        <>
                          {/* Recording Controls */}
                          {/* Manual start button as fallback */}
                          {!isRecording ? (
                            <Button
                              variant="contained"
                              size="large"
                              onClick={handleStart}
                              startIcon={<PlayArrowIcon />}
                              sx={{
                                backgroundColor: "#9D2235",
                                color: "white",
                                fontSize: {
                                  xs: "0.875rem",
                                  sm: "0.95rem",
                                  md: "1rem",
                                },
                                fontWeight: "bold",
                                py: { xs: 1, sm: 1.25, md: 1.5 },
                                px: { xs: 2.5, sm: 3, md: 4 },
                                borderRadius: 25,
                                textTransform: "none",
                                "&:hover": {
                                  backgroundColor: "#7a182a",
                                },
                                boxShadow: "0 4px 16px rgba(157, 34, 53, 0.3)",
                              }}
                            >
                              Start Recording
                            </Button>
                          ) : (
                            <>
                              <IconButton
                                onClick={isPaused ? handleStart : handlePause}
                                sx={{
                                  backgroundColor: "#f0f0f0",
                                  color: "#9D2235",
                                  width: { xs: 44, sm: 50, md: 56 },
                                  height: { xs: 44, sm: 50, md: 56 },
                                  "&:hover": {
                                    backgroundColor: "#e0e0e0",
                                  },
                                }}
                              >
                                {isPaused ? (
                                  <PlayArrowIcon
                                    sx={{ fontSize: { xs: 22, sm: 25, md: 28 } }}
                                  />
                                ) : (
                                  <PauseIcon
                                    sx={{ fontSize: { xs: 22, sm: 25, md: 28 } }}
                                  />
                                )}
                              </IconButton>
                              <Button
                                variant="contained"
                                onClick={handleStop}
                                startIcon={<StopIcon />}
                                sx={{
                                  backgroundColor: "#9D2235",
                                  color: "white",
                                  fontSize: {
                                    xs: "0.875rem",
                                    sm: "0.95rem",
                                    md: "1rem",
                                  },
                                  fontWeight: "bold",
                                  py: { xs: 1, sm: 1.25, md: 1.5 },
                                  px: { xs: 2.5, sm: 3, md: 4 },
                                  borderRadius: 25,
                                  textTransform: "none",
                                  "&:hover": {
                                    backgroundColor: "#7a182a",
                                  },
                                  boxShadow:
                                    "0 4px 16px rgba(157, 34, 53, 0.3)",
                                }}
                              >
                                Stop
                              </Button>
                            </>
                          )}
                        </>
                      )}

                      {/* Playback button for recorded audio - only show if not already submitted */}
                      {recordedUrl && !isRecording && !isSpeakingSubmitted && (
                        <IconButton
                          onClick={() => {
                            if (audioRef.current) {
                              if (isPlaying) {
                                audioRef.current.pause();
                                setIsPlaying(false);
                              } else {
                                audioRef.current.play();
                                setIsPlaying(true);
                              }
                            }
                          }}
                          sx={{
                            backgroundColor: "#9D2235",
                            color: "#fff",
                            width: { xs: 44, sm: 50, md: 56 },
                            height: { xs: 44, sm: 50, md: 56 },
                            "&:hover": {
                              backgroundColor: "#7a182a",
                            },
                            boxShadow: "0 4px 12px rgba(157,34,53,0.25)",
                          }}
                        >
                          {isPlaying ? (
                            <PauseIcon
                              sx={{ fontSize: { xs: 22, sm: 25, md: 28 } }}
                            />
                          ) : (
                            <PlayArrowIcon
                              sx={{ fontSize: { xs: 22, sm: 25, md: 28 } }}
                            />
                          )}
                        </IconButton>
                      )}

                      {/* Delete recording button - only show if not already submitted */}
                      {recordedUrl && !isRecording && !isSpeakingSubmitted && (
                        <IconButton
                          onClick={handleDeleteRecording}
                          sx={{
                            backgroundColor: "#f0f0f0",
                            color: "#d32f2f",
                            width: { xs: 44, sm: 50, md: 56 },
                            height: { xs: 44, sm: 50, md: 56 },
                            "&:hover": {
                              backgroundColor: "#e0e0e0",
                            },
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: { xs: 22, sm: 25, md: 28 } }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                )}
              </TextHighlighterWrapper>
            </CardContent>
          </Card>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg shadow-md p-6 mb-8">
            <div className="text-lg font-bold mb-2">
              Speaking Test - Part{" "}
              {currentSpeakingPart.partNumber || currentCardIndex + 1}
            </div>
            <div className="text-base">
              Prepare your speaking response for Part{" "}
              {currentSpeakingPart.partNumber || currentCardIndex + 1}.
            </div>
          </div>
        )}

        {/* Fixed Footer Navigation Actions */}
        <Box
          sx={{
            position: "fixed",
            left: 0,
            bottom: 0,
            width: "100%",
            bgcolor: "#F7F7F7",
            boxShadow: "0px -4px 12px rgba(0,0,0,0.05)",
            px: { xs: 2, sm: 4 },
            py: isShortScreen ? 1 : 2,
            zIndex: 30,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* Show navigation buttons - disable submit when already submitted */}
          {/* In simulation mode with speaking already submitted, show Finish button */}
          {isSpeakingSubmitted && isLastQuestionInPart && isLastCard ? (
            <Button
              variant="contained"
              onClick={() => {
                // Show results popup if handler is available, otherwise navigate directly
                if (onShowResultsPopup) {
                  onShowResultsPopup();
                } else if (testId) {
                  // Detect test type from URL
                  const path = location.pathname.toLowerCase();
                  let resultsPath;
                  if (path.includes("/mini-mock-test/")) {
                    resultsPath = `/mini-mock-test/${testId}/results`;
                  } else if (path.includes("/class-test/")) {
                    resultsPath = `/class-test/${testId}/results`;
                  } else {
                    resultsPath = `/mock-test/${testId}/results`;
                  }
                  navigationRef.current(resultsPath);
                } else {
                  navigationRef.current("/dashboard");
                }
              }}
              size="large"
              sx={{
                py: isShortScreen ? 1 : 1.5,
                px: isShortScreen ? 3 : 4,
                fontSize: isShortScreen ? "0.95rem" : "1.1rem",
                fontWeight: "bold",
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
              }}
            >
              Finish
            </Button>
          ) : isLastQuestionInPart && isLastCard ? (
            <Button
              variant="contained"
              onClick={handleNextOrSubmit}
              disabled={
                isSpeakingSubmitted ||
                !recordedBlob ||
                isUploading ||
                isSubmitting
              }
              size="large"
              sx={{
                py: isShortScreen ? 1 : 1.5,
                px: isShortScreen ? 3 : 4,
                fontSize: isShortScreen ? "0.95rem" : "1.1rem",
                fontWeight: "bold",
                backgroundColor: isSpeakingSubmitted ? "#cccccc" : "#9D2235",
                "&:hover": {
                  backgroundColor: isSpeakingSubmitted ? "#cccccc" : "#7a182a",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {isSpeakingSubmitted
                ? "Already Submitted"
                : isUploading || isSubmitting
                  ? "Submitting..."
                  : "Submit Speaking"}
            </Button>
          ) : isLastQuestionInPart ? (
            <Button
              variant="contained"
              onClick={() => {
                // If already submitted, just navigate without recording check
                if (isSpeakingSubmitted) {
                  setCurrentCardIndex((prev) => prev + 1);
                  setCurrentQuestionIndex(0);
                } else {
                  handleNextOrSubmit();
                }
              }}
              disabled={
                !isSpeakingSubmitted &&
                (!recordedBlob || isUploading || isSubmitting)
              }
              endIcon={<ArrowForwardIcon />}
              size="large"
              sx={{
                py: isShortScreen ? 1 : 1.5,
                px: isShortScreen ? 3 : 4,
                fontSize: isShortScreen ? "0.95rem" : "1.1rem",
                fontWeight: "bold",
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {!isSpeakingSubmitted && (isUploading || isSubmitting)
                ? "Processing..."
                : `Next Part (${currentCardIndex + 2})`}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                // If already submitted, just navigate without recording check
                if (isSpeakingSubmitted) {
                  setCurrentQuestionIndex((prev) => prev + 1);
                } else {
                  handleNextOrSubmit();
                }
              }}
              disabled={
                !isSpeakingSubmitted &&
                (!recordedBlob || isUploading || isSubmitting)
              }
              endIcon={<ArrowForwardIcon />}
              size="large"
              sx={{
                py: isShortScreen ? 1 : 1.5,
                px: isShortScreen ? 3 : 4,
                fontSize: isShortScreen ? "0.95rem" : "1.1rem",
                fontWeight: "bold",
                backgroundColor: "#9D2235",
                "&:hover": {
                  backgroundColor: "#7a182a",
                },
                "&:disabled": {
                  backgroundColor: "#cccccc",
                },
              }}
            >
              {!isSpeakingSubmitted && (isUploading || isSubmitting)
                ? "Processing..."
                : `Next Question`}
            </Button>
          )}
        </Box>

        {/* ...existing code... */}

        {/* Success Dialog */}
        <Dialog open={successOpen}>
          <DialogTitle>Success!</DialogTitle>
          <Box sx={{ p: 2 }}>
            <Typography>
              Your speaking responses have been submitted successfully.
            </Typography>
          </Box>
        </Dialog>

        {/* Submission Response Popup */}
        <Dialog
          open={submissionPopupOpen}
          onClose={handleCloseSubmissionPopup}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxHeight: "80vh",
            },
          }}
        >
          <DialogTitle
            sx={{
              textAlign: "center",
              bgcolor: "#9D2235", // Use consistent theme color for all states
              color: "white",
              py: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              {submissionResponse?.data?.message &&
              submissionResponse.data.message.includes("already completed") ? (
                <WarningIcon sx={{ fontSize: 28 }} />
              ) : submissionResponse?.data?.success === 0 ||
                submissionResponse?.status >= 400 ||
                submissionResponse?.status === "error" ? (
                <ErrorIcon sx={{ fontSize: 28 }} />
              ) : (
                <CheckCircleIcon sx={{ fontSize: 28 }} />
              )}
              <Typography variant="h6" fontWeight="bold">
                {submissionResponse?.data?.message &&
                submissionResponse.data.message.includes("already completed")
                  ? "Submission Notice"
                  : submissionResponse?.data?.success === 0 ||
                      submissionResponse?.status >= 400 ||
                      submissionResponse?.status === "error"
                    ? "Upload Error"
                    : "Submission Message"}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            {/* Handle different response types - Check for 'already completed' first */}
            {submissionResponse?.data?.message &&
            submissionResponse.data.message.includes("already completed") ? (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #ffecb3",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Test Already Completed
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {submissionResponse.data.message}
                </Typography>
              </Alert>
            ) : submissionResponse?.data?.success === 0 ||
              submissionResponse?.status >= 400 ||
              submissionResponse?.status === "error" ? (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #ffcdd2",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Upload Failed
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {/* Extract specific error message */}
                  {submissionResponse?.data?.data?.error ||
                    submissionResponse?.data?.message ||
                    submissionResponse?.message ||
                    "An error occurred while submitting your response."}
                </Typography>
              </Alert>
            ) : (
              <Alert
                severity="success"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography variant="body1" fontWeight="600" sx={{ mb: 1 }}>
                  Message !
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {/* Display actual API response message */}
                  {submissionResponse?.data?.message ||
                    submissionResponse?.message ||
                    "Your audio responses are now being processed for evaluation."}
                </Typography>
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, justifyContent: "center" }}>
            {/* Show different button based on response type - Check for 'already completed' first */}
            {(submissionResponse?.data?.message &&
              submissionResponse.data.message.includes("already completed")) ||
            submissionResponse?.data?.success === 0 ||
            submissionResponse?.status >= 400 ||
            submissionResponse?.status === "error" ? (
              <Button
                onClick={() => setSubmissionPopupOpen(false)}
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                sx={{
                  backgroundColor: "#9D2235",
                  "&:hover": {
                    backgroundColor: "#7a182a",
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                Try Again
              </Button>
            ) : (
              <Button
                onClick={handleCloseSubmissionPopup}
                variant="contained"
                size="large"
                startIcon={<CheckCircleIcon />}
                sx={{
                  backgroundColor: "#9D2235",
                  "&:hover": {
                    backgroundColor: "#7a182a",
                  },
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                Return to Test Menu
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Notes Modal */}
        <NotesModal
          open={notesModalOpen}
          onClose={() => setNotesModalOpen(false)}
          notes={notes}
        />

        {/* Hidden audio element for playback */}
        {recordedUrl && (
          <audio
            ref={audioRef}
            src={recordedUrl}
            onEnded={() => setIsPlaying(false)}
            style={{ display: "none" }}
          />
        )}

        {/* CSS Animations */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% { 
                opacity: 1; 
                transform: scale(1);
              }
              50% { 
                opacity: 0.7;
                transform: scale(0.95);
              }
            }
          `}
        </style>
      </Box>
    </>
  );
}
